import React, { useState, useEffect } from 'react';
import { Table } from '../components/ui/Table';
import { Download, ChevronDown, Search, X } from 'lucide-react';
import {
  listBilling,
  getBillingStats,
  exportOrders,
  listRestaurants,
  listGroceries
} from '../services/resortAdminApi';
import { useAuth } from '../context/AuthContext';

interface BillingOrder {
  order_id: string;
  order_date: string;
  status: string;
  resort_name: string;
  pos_name: string;
  full_order_amount: number | null;
  app_fee: number | null;
  order_price: number | null;
  platform_fee: number;
  partner_payout: number;
}

interface BillingStats {
  total_orders: number;
  total_revenue?: number;
  total_app_fees?: number;
  total_order_price?: number;
  platform_fees: number;
  partner_payout: number;
  platform_fee_rate: number;
}

interface Restaurant {
  name: string;
  restaurant_name: string;
}

interface Grocery {
  name: string;
  grocery_name: string;
}

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
  { value: '365', label: 'Last Year' },
  { value: 'all', label: 'All Time' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'claimed', label: 'Claimed' },
  { value: 'ready_for_delivery', label: 'Ready For Delivery' },
  { value: 'out_for_delivery', label: 'Out For Delivery' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'reported', label: 'Reported' }
];

export const Billing: React.FC = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState<BillingOrder[]>([]);
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [groceries, setGroceries] = useState<Grocery[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const isSuperAdmin = user?.role === 'Resort Super Admin';

  // Determine what the user can access based on role
  const canSeeRestaurants = user?.role === 'Resort Super Admin' ||
    user?.role === 'Partner Admin';
  const canSeeGroceries = user?.role === 'Resort Super Admin' ||
    user?.role === 'Partner Admin';

  // Filters
  const [restaurantFilter, setRestaurantFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = useState<string>('30');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load restaurants and groceries for filter dropdown based on user role
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const promises: Promise<any>[] = [];

        if (canSeeRestaurants) {
          promises.push(listRestaurants(undefined, 1, 1000));
        } else {
          promises.push(Promise.resolve(null));
        }

        if (canSeeGroceries) {
          promises.push(listGroceries(undefined, 1, 1000));
        } else {
          promises.push(Promise.resolve(null));
        }

        const [restaurantsResult, groceriesResult] = await Promise.all(promises);

        if (restaurantsResult?.data) {
          setRestaurants(restaurantsResult.data);
        }
        if (groceriesResult?.data) {
          setGroceries(groceriesResult.data);
        }
      } catch (err) {
        console.error('Error loading filters:', err);
      }
    };
    loadFilters();
  }, [canSeeRestaurants, canSeeGroceries]);

  // Build filters object
  const buildFilters = () => {
    const filters: any = {};

    if (restaurantFilter) {
      filters.restaurant = restaurantFilter;
    }
    if (statusFilter) {
      filters.status = statusFilter;
    }
    if (dateRangeFilter && dateRangeFilter !== 'all') {
      const days = parseInt(dateRangeFilter);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      filters.start_date = startDate.toISOString().split('T')[0];
      filters.end_date = endDate.toISOString().split('T')[0];
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  // Load billing data
  const loadBilling = async () => {
    setLoading(true);
    try {
      const filters = buildFilters();
      const searchTerm = search.trim() || undefined;
      const result = await listBilling(filters, searchTerm, page, pageSize);

      if (result && result.data) {
        setOrders(result.data);
        setTotal(result.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error loading billing:', error);
      setError(error.message || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const filters = buildFilters();
      const result = await getBillingStats(filters);

      if (result) {
        setStats(result);
      }
    } catch (error: any) {
      console.error('Error loading billing stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
    loadStats();
  }, [page, pageSize, restaurantFilter, statusFilter, dateRangeFilter, search]);

  // Handle export
  const handleExport = async () => {
    try {
      const filters = buildFilters();
      const blob = await exportOrders(filters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `billing-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error exporting billing:', error);
      setError(error.message || 'Failed to export billing data');
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return '-';
    return `€${amount.toFixed(2)}`;
  };

  // Statuses that represent completed orders (eligible for billing breakdown)
  const isCompletedOrder = (status: string) => ['delivered', 'cc_completed'].includes(status);

  // Build columns based on user role
  const columns: any[] = [
    {
      key: 'resort_name',
      header: 'Resort',
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value || '-'}</span>
      )
    },
    {
      key: 'pos_name',
      header: 'POS',
      render: (value: string) => (
        <span className="text-gray-600">{value || '-'}</span>
      )
    },
    {
      key: 'order_id',
      header: 'Order ID',
      render: (value: string) => (
        <span className="text-gray-600">{value}</span>
      )
    },
    {
      key: 'order_date',
      header: 'Order Date',
      render: (value: string) => formatDate(value)
    },
    {
      key: 'status',
      header: 'Status',
      render: (value: string) => {
        const statusStyles: Record<string, string> = {
          'delivered': 'bg-green-100 text-green-800',
          'accepted': 'bg-blue-100 text-blue-800',
          'claimed': 'bg-blue-100 text-blue-800',
          'ready_for_delivery': 'bg-yellow-100 text-yellow-800',
          'out_for_delivery': 'bg-purple-100 text-purple-800',
          'cancelled': 'bg-gray-100 text-gray-800',
          'reported': 'bg-red-100 text-red-800',
          'payment_successful': 'bg-blue-100 text-blue-800',
          'cc_accepted': 'bg-blue-100 text-blue-800',
          'cc_claimed': 'bg-blue-100 text-blue-800',
          'cc_ready_for_pickup': 'bg-yellow-100 text-yellow-800',
          'cc_completed': 'bg-green-100 text-green-800',
          'cc_cancelled': 'bg-gray-100 text-gray-800',
          'cc_reported': 'bg-red-100 text-red-800',
        };
        const label = value ? value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Draft';
        return (
          <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${statusStyles[value] || 'bg-gray-100 text-gray-800'}`}>
            {label}
          </span>
        );
      }
    }
  ];

  // Add role-specific columns
  if (isSuperAdmin) {
    columns.push(
      {
        key: 'full_order_amount',
        header: 'Price',
        render: (value: number | null, row: BillingOrder) => (
          <span className="font-medium text-gray-900">
            {isCompletedOrder(row.status) ? formatCurrency(value) : '-'}
          </span>
        )
      },
      {
        key: 'app_fee',
        header: 'App Fee',
        render: (value: number | null, row: BillingOrder) => (
          <span className="text-gray-600">
            {isCompletedOrder(row.status) ? formatCurrency(value) : '-'}
          </span>
        )
      }
    );
  } else {
    columns.push(
      {
        key: 'order_price',
        header: 'Order Price',
        render: (value: number | null, row: BillingOrder) => (
          <span className="font-medium text-gray-900">
            {isCompletedOrder(row.status) ? formatCurrency(value) : '-'}
          </span>
        )
      }
    );
  }

  columns.push(
    {
      key: 'platform_fee',
      header: 'Platform Fee',
      render: (value: number, row: BillingOrder) => (
        <span className="text-gray-600">
          {isCompletedOrder(row.status) ? formatCurrency(value) : '-'}
        </span>
      )
    },
    {
      key: 'partner_payout',
      header: 'Partner Payout',
      render: (value: number, row: BillingOrder) => (
        <span className="font-medium text-green-600">
          {isCompletedOrder(row.status) ? formatCurrency(value) : '-'}
        </span>
      )
    }
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Billing</h1>
          <p className="mt-1 text-sm text-gray-600">Financial overview and fee calculations</p>
        </div>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark"
        >
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Orders */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Amount</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {statsLoading ? '-' : formatCurrency(isSuperAdmin ? stats?.total_revenue : stats?.total_order_price)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Total Orders: {statsLoading ? '-' : (stats?.total_orders ?? 0)}
            </p>
          </div>
        </div>

        {/* Total App Fees */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div>
            <p className="text-sm font-medium text-gray-600">
              {isSuperAdmin ? 'Total App Fees' : 'Total Order Price'}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {statsLoading ? '-' : formatCurrency(isSuperAdmin ? stats?.total_app_fees : stats?.total_order_price)}
            </p>
          </div>
        </div>

        {/* Platform Fees */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div>
            <p className="text-sm font-medium text-gray-600">Platform Fees</p>
            <p className="text-3xl font-bold text-gray-900 mt-2">
              {statsLoading ? '-' : formatCurrency(stats?.platform_fees ?? 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {stats?.platform_fee_rate ?? 10}% commission
            </p>
          </div>
        </div>

        {/* Partner Payout */}
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <div>
            <p className="text-sm font-medium text-gray-600">Partner Payout</p>
            <p className="text-3xl font-bold text-green-600 mt-2">
              {statsLoading ? '-' : formatCurrency(stats?.partner_payout ?? 0)}
            </p>
            <p className="text-xs text-gray-500 mt-1">Net amount</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          {/* Restaurant/Grocery Filter */}
          <div className="relative">
            <select
              value={restaurantFilter}
              onChange={(e) => {
                setRestaurantFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              <option value="">
                {canSeeRestaurants && canSeeGroceries
                  ? 'All Restaurants/Groceries'
                  : canSeeGroceries
                    ? 'All Groceries'
                    : 'All Restaurants'}
              </option>
              {restaurants.map((r) => (
                <option key={r.name} value={r.name}>{r.restaurant_name}</option>
              ))}
              {groceries.length > 0 && (
                <>
                  {restaurants.length > 0 && <option disabled>---</option>}
                  {groceries.map((g) => (
                    <option key={g.name} value={g.name}>{g.grocery_name}</option>
                  ))}
                </>
              )}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Date Range Filter */}
          <div className="relative">
            <select
              value={dateRangeFilter}
              onChange={(e) => {
                setDateRangeFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Order ID Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search order ID..."
              className="w-full bg-white border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput('')}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Billing Table */}
      <Table
        columns={columns}
        data={orders}
        isLoading={loading}
        emptyMessage="No billing data found"
        pagination={{
          page,
          page_size: pageSize,
          total
        }}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
};
